import { useState, useCallback } from "react";
import { getCurrentTimestamp } from "../utils/dateHelpers";

/**
 * New activity form state hook
 * FIXED: Date formatting to YYYY-MM-DD before submission
 * FIXED: Strip timezone from date when loading activity for editing
 * UPDATED: Added documents field
 */
const useFormState = (userId) => {
  const [formData, setFormData] = useState({
    id: null,
    title: "",
    date: "",
    type: "",
    content: "",
    drawing: null,
    images: [],
    documents: [],
    competencies: [],
    competencyAddress: "",
    attemptType: "",
    procedureSubtype: "",
    procedureCategory: "",
    userId: userId,
    draftId: null,
  });

  const [isDirty, setIsDirty] = useState(false);

  /**
   * Update form field
   */
  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  }, []);

  /**
   * Update multiple fields
   */
  const updateFields = useCallback((updates) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
    setIsDirty(true);
  }, []);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData({
      id: null,
      title: "",
      date: "",
      type: "",
      content: "",
      drawing: null,
      images: [],
      documents: [],
    competencies: [],
      competencyAddress: "",
      attemptType: "",
      procedureSubtype: "",
      procedureCategory: "",
      userId: userId,
      draftId: null,
    });
    setIsDirty(false);
  }, [userId]);

  /**
   * Load existing activity for editing
   * FIXED: Strip timezone from date field (2026-05-07T00:00:00+00:00 → 2026-05-07)
   */
  const loadActivity = useCallback(
    (activity) => {
      setFormData({
        ...activity,
        date: activity.date ? activity.date.split("T")[0] : "",
        documents: activity.documents || [],
        competencies: activity.competencies || activity.competencies_json || [],
        procedureSubtype:
          activity.procedure_sub_type || activity.procedureSubtype || "",
        procedureCategory:
          activity.procedure_category || activity.procedureCategory || "",
        userId: userId,
      });
      setIsDirty(false);
    },
    [userId],
  );

  /**
   * Load draft - DEFENSIVE: Handle both wrapped and flat draft formats
   */
  const loadDraft = useCallback(
    (draft) => {
      console.log("🔍 Loading draft:", draft); // DEBUG

      // Extract data from wrapped format if needed
      const draftData = draft.data || draft;

      console.log("📦 Draft data extracted:", draftData); // DEBUG

      setFormData({
        title: draftData.title || "",
        date: draftData.date || "",
        type: draftData.type || "",
        content: draftData.content || "",
        drawing: draftData.drawing || null,
        images: draftData.images || [],
        documents: draftData.documents || [],
        competencies: draftData.competencies || draftData.competencies_json || [],
        competencyAddress:
          draftData.competency_address || draftData.competencyAddress || "",
        attemptType: draftData.attempt_type || draftData.attemptType || "",
        procedureSubtype:
          draftData.procedure_sub_type || draftData.procedureSubtype || "",
        procedureCategory:
          draftData.procedure_category || draftData.procedureCategory || "",
        userId: userId,
        draftId: draftData.id || null,
      });
      setIsDirty(false);
    },
    [userId],
  );

  /**
   * Prepare data for API submission
   * FIXED: Format date to YYYY-MM-DD before sending to server
   */
  const prepareForSubmit = useCallback(() => {
    const timestamp = getCurrentTimestamp();

    // Format date to YYYY-MM-DD if it's a Date object
    let formattedDate;
    if (formData.date) {
      if (typeof formData.date === "string") {
        // Already a string - ensure it's just YYYY-MM-DD (strip any timezone if present)
        formattedDate = formData.date.split("T")[0];
      } else if (formData.date instanceof Date) {
        // Date object from DatePicker - convert to YYYY-MM-DD
        formattedDate = formData.date.toISOString().split("T")[0];
      } else {
        // Fallback
        formattedDate = timestamp;
      }
    } else {
      formattedDate = timestamp;
    }

    return {
      ...(formData.id && /^\d+$/.test(String(formData.id))
        ? { id: formData.id }
        : {}),
      title: formData.title,
      date: formattedDate,
      type: formData.type,
      content: formData.content,
      competencyAddress: formData.competencyAddress,
      attemptType: formData.attemptType,
      procedure_sub_type: formData.procedureSubtype || "",
      procedureSubtype: formData.procedureSubtype || "",
      procedure_category: formData.procedureCategory || "",
      procedureCategory: formData.procedureCategory || "",
      drawing: formData.drawing,
      images: formData.images,
      documents: formData.documents,
      competencies: formData.competencies || [],
      createdTimestamp: formData.createdTimestamp || timestamp,
      timestamp: timestamp,
      draft_id: formData.draftId || null,
    };
  }, [formData]);

  return {
    formData,
    isDirty,
    updateField,
    updateFields,
    resetForm,
    loadActivity,
    loadDraft,
    prepareForSubmit,
  };
};

export default useFormState;

//8
